import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Fade,
  Alert,
  InputAdornment,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Navbar from '../components/Navbar.js';
import { ENDPOINTS, apiCall } from '../utils/api.js';
import PhoneField, { defaultCountry, composeE164 } from '../components/PhoneField.js';

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  businessName: '',
};

const EMPTY_ERRORS = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  businessName: '',
};

function validate(fields) {
  const errs = { ...EMPTY_ERRORS };
  if (!fields.firstName.trim()) errs.firstName = 'First name is required.';
  if (!fields.lastName.trim()) errs.lastName = 'Last name is required.';
  if (!fields.email.trim()) {
    errs.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) {
    errs.email = 'Please enter a valid email address.';
  }
  if (!fields.phone.trim()) {
    errs.phone = 'Phone number is required.';
  } else if (!/^[+\d\s\-().]{6,20}$/.test(fields.phone.trim())) {
    errs.phone = 'Please enter a valid phone number.';
  }
  if (!fields.businessName.trim()) errs.businessName = 'Business name is required.';
  return errs;
}

function hasErrors(errs) {
  return Object.values(errs).some(Boolean);
}

export default function ContactPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [submitError, setSubmitError] = useState('');
  const [country, setCountry] = useState(defaultCountry);

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    // Re-validate live once the field has been touched
    if (touched[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: validate({ ...form, [field]: val })[field],
      }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validate(form)[field] }));
  };

  const handleSubmit = async () => {
    // Mark all fields touched so errors show
    setTouched({ firstName: true, lastName: true, email: true, phone: true, businessName: true });
    const errs = validate(form);
    setErrors(errs);
    if (hasErrors(errs)) return;

    setSubmitStatus('loading');
    setSubmitError('');

    try {
      await apiCall(ENDPOINTS.ONBOARDING_ENQUIRY, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.firstName.trim(),
          last_name: form.lastName.trim(),
          email: form.email.trim(),
          phone_number: composeE164(country, form.phone),
          business_name: form.businessName.trim(),
        }),
      });
      setSubmitStatus('success');
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      setSubmitStatus('error');
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'background.paper' },
  };

  // ── Success screen ────────────────────────────────────────────────────────
  if (submitStatus === 'success') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        <Navbar />
        <Fade in timeout={400}>
          <Box
            sx={{
              maxWidth: 520,
              mx: 'auto',
              px: { xs: 2, sm: 3 },
              py: { xs: 8, sm: 12 },
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={800}>
              Message received!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Thanks for reaching out. One of our team members will be in touch within one business
              day.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ mt: 1, borderRadius: 2.5, fontWeight: 600 }}
            >
              Back to home
            </Button>
          </Box>
        </Fade>
      </Box>
    );
  }

  // ── Main form ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Navbar />

      <Box
        sx={{
          maxWidth: 720,
          mx: 'auto',
          px: { xs: 2, sm: 3 },
          py: { xs: 5, sm: 8 },
          display: 'flex',
          flexDirection: 'column',
          gap: 5,
        }}
      >
        {/* ── Header ───────────────────────────────────────────────────── */}
        <Fade in timeout={400}>
          <Box>
            <Chip
              label="Business enquiries"
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mb: 2, fontWeight: 600, fontSize: 12 }}
            />
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{ letterSpacing: '-0.02em', mb: 1.5, lineHeight: 1.2 }}
            >
              Let's get your business on sqip.
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 540 }}>
              Fill in your details below and our team will reach out to get you set up. Whether
              you're a single-chair salon or a multi-room clinic, we'll find the right plan for you.
            </Typography>
          </Box>
        </Fade>

        {/* ── Form card ────────────────────────────────────────────────── */}
        <Fade in timeout={500}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 4,
              px: { xs: 2.5, sm: 4 },
              py: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            {/* ── Your name ──────────────────────────────────────────── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
                Your name
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First name"
                    value={form.firstName}
                    onChange={handleChange('firstName')}
                    onBlur={handleBlur('firstName')}
                    error={Boolean(touched.firstName && errors.firstName)}
                    helperText={touched.firstName && errors.firstName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last name"
                    value={form.lastName}
                    onChange={handleChange('lastName')}
                    onBlur={handleBlur('lastName')}
                    error={Boolean(touched.lastName && errors.lastName)}
                    helperText={touched.lastName && errors.lastName}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PersonOutlineIcon fontSize="small" color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* ── Contact details ────────────────────────────────────── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
                Contact details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Email address"
                  type="email"
                  value={form.email}
                  onChange={handleChange('email')}
                  onBlur={handleBlur('email')}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={inputSx}
                />
                <PhoneField
                  country={country}
                  setCountry={setCountry}
                  phone={form.phone}
                  setPhone={(v) => handleChange('phone')({ target: { value: v } })}
                  onBlur={handleBlur('phone')}
                  error={Boolean(touched.phone && errors.phone)}
                  helperText={touched.phone && errors.phone}
                />
              </Box>
            </Box>

            <Divider />

            {/* ── Business details ───────────────────────────────────── */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5, color: 'text.primary' }}>
                Business details
              </Typography>
              <TextField
                fullWidth
                label="Business name"
                value={form.businessName}
                onChange={handleChange('businessName')}
                onBlur={handleBlur('businessName')}
                error={Boolean(touched.businessName && errors.businessName)}
                helperText={touched.businessName && errors.businessName}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <BusinessOutlinedIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
            </Box>

            {/* ── Error alert ────────────────────────────────────────── */}
            {submitStatus === 'error' && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}

            {/* ── Submit ─────────────────────────────────────────────── */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={
                submitStatus === 'loading' ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendOutlinedIcon />
                )
              }
              onClick={handleSubmit}
              disabled={submitStatus === 'loading'}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                color: '#fff',
                py: 1.5,
                alignSelf: { xs: 'stretch', sm: 'flex-end' },
                minWidth: 180,
              }}
            >
              {submitStatus === 'loading' ? 'Sending…' : 'Send enquiry'}
            </Button>
          </Box>
        </Fade>

        {/* ── Footer note ──────────────────────────────────────────────── */}
        <Fade in timeout={600}>
          <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center' }}>
            We typically respond within one business day. Your information is never shared with
            third parties.
          </Typography>
        </Fade>
      </Box>
    </Box>
  );
}
